import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { format, parse, startOfWeek, getDay, parseISO } from "date-fns";
import { pl } from "date-fns/locale";
import { RefreshCcw, X } from "lucide-react";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import "../styles/LecturerCalendar.css";
import { escapeHtml, roomDetailLines, roomTooltipHtml } from "../utils/roomDisplay.js";

const localizer = dateFnsLocalizer({
  format: (date, formatStr, options) =>
    format(date, formatStr, { ...options, locale: pl }),
  parse: (value, formatStr, backupDate, options) =>
    parse(value, formatStr, backupDate, { ...options, locale: pl }),
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 1, locale: pl }),
  getDay,
  locales: { pl },
});

const messages = {
  today: "Dzisiaj",
  previous: "Poprzedni",
  next: "Następny",
  month: "Miesiąc",
  week: "Tydzień",
  day: "Dzień",
  agenda: "Agenda",
  date: "Data",
  time: "Godzina",
  event: "Zajęcia",
  allDay: "Cały dzień",
  noEventsInRange: "Brak wydarzeń w tym zakresie.",
};

const colorPalette = [
  "#3b82f6",
  "#ef4444",
  "#22c55e",
  "#f59e0b",
  "#8b5cf6",
  "#06b6d4",
  "#e11d48",
  "#84cc16",
];

const randomColor = () =>
  "#" + Math.floor(Math.random() * 16777215).toString(16);

const CustomEvent = ({ event }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      const roomBlock = roomTooltipHtml(
        event.room_name,
        event.room_location,
        event.room_comment
      );
      tippy(ref.current, {
        content: `
          <div>
            <strong>${escapeHtml(event.title)}</strong><br/>
            ${escapeHtml(format(event.start, "HH:mm"))} - ${escapeHtml(format(event.end, "HH:mm"))}<br/>
            ${roomBlock}
          </div>
        `,
        allowHTML: true,
        theme: "light-border",
        placement: "top",
      });
    }
  }, [event]);

  return <div ref={ref}>{event.module}</div>;
};

const EventModal = ({ event, onClose }) => {
  if (!event) return null;

  return (
    <div className="calendar-modal-overlay">
      <div className="calendar-modal-content">
        <div className="calendar-modal-header">
          <h2>{event.title}</h2>
          <button className="calendar-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="calendar-modal-body">
          <p>
            <strong>Moduł:</strong> {event.module}
          </p>
          <p>
            <strong>Grupa:</strong> {event.group}
          </p>
          {roomDetailLines(
            event.room_name,
            event.room_location,
            event.room_comment
          ).map((line, i) => (
            <p key={i}>
              <strong>{line.label}:</strong> {line.value}
            </p>
          ))}
          <p>
            <strong>Data:</strong> {format(event.start, "dd.MM.yyyy")}
            <br />
            <strong>Godziny:</strong>{" "}
            {format(event.start, "HH:mm")} - {format(event.end, "HH:mm")}
          </p>
          <p>
            <strong>Opis:</strong>
            <br />
            {event.description || "Brak opisu"}
          </p>

          {event.assignments?.length > 0 && (
            <>
              <hr />
              <h4>📚 Zadania powiązane:</h4>
              <ul>
                {event.assignments.map((a) => (
                  <li key={a.assignment_id}>
                    <strong>{a.title}</strong> — termin:{" "}
                    {format(parseISO(a.due_date), "dd.MM.yyyy")}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== GŁÓWNY KOMPONENT =====
export default function TeacherCalendar() {
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState("month"); // domyślnie MIESIĄC
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [moduleColors, setModuleColors] = useState(() => {
    const saved = localStorage.getItem("teacherModuleColors");
    return saved ? JSON.parse(saved) : {};
  });

  const token = localStorage.getItem("token");

  const assignColors = (sessions) => {
    setModuleColors((prev) => {
      const updated = { ...prev };
      sessions.forEach((s) => {
        if (!updated[s.module]) {
          const newColor =
            colorPalette[Object.keys(updated).length % colorPalette.length] ||
            randomColor();
          updated[s.module] = newColor;
        }
      });
      localStorage.setItem("teacherModuleColors", JSON.stringify(updated));
      return updated;
    });
  };

  const eventStyleGetter = (event) => {
    const backgroundColor = moduleColors[event.module] || "#6b7280";
    return {
      style: {
        backgroundColor,
        color: "white",
        borderRadius: "8px",
        border: "none",
        padding: "2px 4px",
      },
    };
  };

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/lecturer/dashboard/calendar`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        }
      );

      if (!Array.isArray(res.data)) {
        console.error("Nieprawidłowy format danych:", res.data);
        setEvents([]);
        return;
      }

      const data = res.data.map((row, i) => ({
        id: i,
        title: row.session_title || row.title || "Zajęcia",
        start: parseISO(row.start),
        end: parseISO(row.end),
        module: row.module_name || row.title || "---",
        description: row.session_description || "---",
        room_name: row.room,
        room_location: row.room_location,
        room_comment: row.room_comment,
        group: row.group || "---",
        assignments: row.assignments || [],
      }));

      setEvents(data);
      assignColors(data);
    } catch (err) {
      console.error("❌ Błąd pobierania kalendarza:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  if (loading) return <div>Ładowanie kalendarza...</div>;

  // widok tygodnia/dnia od 7:00
  const minTime = new Date(1970, 0, 1, 7, 0, 0);
  const maxTime = new Date(1970, 0, 1, 21, 0, 0);

  return (
    <div className="lecturer-calendar-container">
      {/* tylko przycisk Odśwież – bez dodatkowego nagłówka */}
      <div className="lecturer-calendar-header-row">
        <button className="refresh-btn" onClick={fetchEvents}>
          <RefreshCcw size={14} />
          <span>Odśwież</span>
        </button>
      </div>

      <div className="lecturer-calendar-wrapper">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          view={currentView}
          date={currentDate}
          onView={(view) => setCurrentView(view)}
          onNavigate={(date) => setCurrentDate(date)}
          views={["month", "week", "day"]}
          messages={messages}
          components={{ event: CustomEvent }}
          eventPropGetter={eventStyleGetter}
          onSelectEvent={(event) => setSelectedEvent(event)}
          min={minTime}
          max={maxTime}
        />
      </div>

      {selectedEvent && (
        <EventModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </div>
  );
}
